// Rewards Shop item definitions
// Users spend Growth Points (GP) earned from lessons, challenges, etc.

export type ShopItemCategory = 'utility' | 'cosmetic' | 'boost';

export interface ShopItem {
  id: string;
  name: string;
  description: string;
  price: number;
  category: ShopItemCategory;
  icon: string; // emoji for display
  maxOwned?: number; // max inventory limit (undefined = unlimited)
  consumable: boolean; // true = used up on activation, false = permanent unlock
  levelRequired?: number; // minimum level to purchase
}

export const SHOP_ITEMS: ShopItem[] = [
  // Utility items
  {
    id: 'streak_freeze',
    name: 'Streak Freeze',
    description: 'Protect your streak if you miss a day. Stacks with your free weekly freeze.',
    price: 60,
    category: 'utility',
    icon: 'streak-freeze',
    maxOwned: 5,
    consumable: true,
  },
  {
    id: 'challenge_refresh',
    name: 'Challenge Refresh',
    description: 'Swap today\'s daily challenge for a new random one.',
    price: 40,
    category: 'utility',
    icon: 'challenge-refresh',
    maxOwned: 3,
    consumable: true,
  },

  // Boost items
  {
    id: 'double_gp',
    name: 'Double GP',
    description: 'Earn 2x Growth Points on your next completed activity.',
    price: 80,
    category: 'boost',
    icon: 'double-gp',
    maxOwned: 3,
    consumable: true,
  },
  {
    id: 'streak_shield',
    name: 'Streak Shield',
    description: 'Automatically protects your streak for the next 3 missed days.',
    price: 150,
    category: 'boost',
    icon: 'streak-shield',
    maxOwned: 1,
    consumable: true,
    levelRequired: 3,
  },

  // Cosmetic items (permanent unlocks)
  {
    id: 'theme_neon',
    name: 'Neon Pulse Theme',
    description: 'Unlock a vibrant neon color theme for the app.',
    price: 200,
    category: 'cosmetic',
    icon: 'neon-pulse',
    consumable: false,
    levelRequired: 2,
  },
  {
    id: 'theme_retro',
    name: 'Retro Wave Theme',
    description: 'Unlock a retro synthwave color theme.',
    price: 250,
    category: 'cosmetic',
    icon: 'retro-wave',
    consumable: false,
    levelRequired: 4,
  },
  {
    id: 'title_resilient',
    name: '"Resilient" Title',
    description: 'Display the "Resilient" badge on your profile.',
    price: 120,
    category: 'cosmetic',
    icon: 'resilient',
    consumable: false,
    levelRequired: 3,
  },
  {
    id: 'title_zen_master',
    name: '"Zen Master" Title',
    description: 'Display the "Zen Master" badge on your profile.',
    price: 300,
    category: 'cosmetic',
    icon: 'zen-master',
    consumable: false,
    levelRequired: 6,
  },
];

export function getShopItem(id: string): ShopItem | undefined {
  return SHOP_ITEMS.find(item => item.id === id);
}

export function getShopItemsByCategory(category: ShopItemCategory): ShopItem[] {
  return SHOP_ITEMS.filter(item => item.category === category);
}
